import React, { useEffect } from "react";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import "../AIAutomation.css";

import {
    FiArrowRight,
    FiZap,
    FiCpu,
    FiGitBranch,
    FiCheckCircle,
    FiDatabase,
    FiMessageSquare,
    FiSettings,
    FiClock,
    FiRepeat,
    FiLayers,
    FiActivity,
    FiShoppingBag,
    FiHeart,
    FiTruck,
    FiDollarSign,
    FiBriefcase,
    FiBox,
} from "react-icons/fi";

const AIAutomation = () => {

    useEffect(() => {
        document.title = "Dropgear IO | AI Automation";
    }, []);

    return (
        <>
            <Header />

            <main className="ai-automation-page">

                {/* =====================================================
            HERO
        ===================================================== */}

                <section className="automation-hero">

                    <div className="automation-hero-grid">

                        {/* LEFT */}

                        <div className="automation-hero-content">

                            <span className="automation-eyebrow">
                                AI AUTOMATION
                            </span>

                            <h1>
                                Automate the work
                                <span> that slows you down.</span>
                            </h1>

                            <p>
                                Dropgear IO helps businesses transform repetitive
                                processes into intelligent workflows that can
                                understand information, coordinate tasks, and
                                move work forward with less manual effort.
                            </p>

                            <div className="automation-hero-actions">

                                <a
                                    href="/book-demo"
                                    className="automation-primary-btn"
                                >
                                    Automate a Workflow
                                    <FiArrowRight />
                                </a>

                                <a
                                    href="#automation-capabilities"
                                    className="automation-secondary-btn"
                                >
                                    Explore Automation
                                </a>

                            </div>

                            <div className="automation-hero-trust">

                                <FiCheckCircle />

                                <span>
                                    Designed around practical business workflows
                                </span>

                            </div>

                        </div>


                        {/* RIGHT - AUTOMATION VISUAL */}

                        <div className="automation-visual">

                            <div className="automation-glow"></div>

                            <div className="automation-flow-line flow-line-one"></div>
                            <div className="automation-flow-line flow-line-two"></div>
                            <div className="automation-flow-line flow-line-three"></div>

                            {/* MAIN AUTOMATION CARD */}

                            <div className="automation-dashboard">

                                <div className="automation-dashboard-header">

                                    <div>
                                        <span className="automation-dashboard-label">
                                            AUTOMATION FLOW
                                        </span>

                                        <strong>
                                            Order Processing
                                        </strong>
                                    </div>

                                    <span className="automation-running">
                                        <i></i>
                                        Running
                                    </span>

                                </div>


                                <div className="automation-progress">

                                    <div className="automation-progress-line"></div>

                                    <div className="automation-node active">
                                        <FiDatabase />
                                        <span>Input</span>
                                    </div>

                                    <div className="automation-node active">
                                        <FiCpu />
                                        <span>AI Process</span>
                                    </div>

                                    <div className="automation-node active">
                                        <FiGitBranch />
                                        <span>Decision</span>
                                    </div>

                                    <div className="automation-node">
                                        <FiZap />
                                        <span>Action</span>
                                    </div>

                                </div>


                                <div className="automation-task-card">

                                    <div className="automation-task-icon">
                                        <FiRepeat />
                                    </div>

                                    <div>
                                        <span>
                                            Current task
                                        </span>

                                        <strong>
                                            Validate order information
                                        </strong>
                                    </div>

                                    <div className="automation-task-status">
                                        Processing
                                    </div>

                                </div>


                                <div className="automation-mini-stats">

                                    <div>
                                        <span>Tasks</span>
                                        <strong>24</strong>
                                    </div>

                                    <div>
                                        <span>Completed</span>
                                        <strong>18</strong>
                                    </div>

                                    <div>
                                        <span>Pending</span>
                                        <strong>06</strong>
                                    </div>

                                </div>

                            </div>


                            {/* FLOATING CARDS */}

                            <div className="automation-floating-card automation-float-one">
                                <FiZap />
                                <span>Workflow Triggered</span>
                            </div>

                            <div className="automation-floating-card automation-float-two">
                                <FiCheckCircle />
                                <span>Task Completed</span>
                            </div>

                            <div className="automation-floating-card automation-float-three">
                                <FiActivity />
                                <span>AI Decision</span>
                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            INTRO
        ===================================================== */}

                <section className="automation-intro">

                    <div className="automation-container">

                        <div className="automation-intro-heading">

                            <span>
                                BEYOND SIMPLE AUTOMATION
                            </span>

                            <h2>
                                Automation that can
                                <br />
                                <strong>understand the workflow.</strong>
                            </h2>

                            <p>
                                Traditional automation follows fixed instructions.
                                AI-powered automation can work with information,
                                interpret context, support decisions, and connect
                                different steps of a business process.
                            </p>

                        </div>


                        <div className="automation-intro-grid">

                            <div className="automation-intro-card">

                                <div className="automation-intro-number">
                                    01
                                </div>

                                <FiDatabase />

                                <h3>
                                    Capture
                                </h3>

                                <p>
                                    Bring information from forms, conversations,
                                    applications, documents, or connected systems
                                    into the workflow.
                                </p>

                            </div>


                            <div className="automation-intro-card">

                                <div className="automation-intro-number">
                                    02
                                </div>

                                <FiCpu />

                                <h3>
                                    Understand
                                </h3>

                                <p>
                                    AI can interpret information and identify
                                    the context required for the next workflow step.
                                </p>

                            </div>


                            <div className="automation-intro-card">

                                <div className="automation-intro-number">
                                    03
                                </div>

                                <FiZap />

                                <h3>
                                    Act
                                </h3>

                                <p>
                                    The workflow can trigger defined actions,
                                    update systems, notify teams, or continue
                                    to the next stage.
                                </p>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            CAPABILITIES
        ===================================================== */}

                <section
                    id="automation-capabilities"
                    className="automation-capabilities"
                >

                    <div className="automation-container">

                        <div className="automation-section-heading">

                            <span>
                                AUTOMATION CAPABILITIES
                            </span>

                            <h2>
                                Build workflows that
                                <br />
                                <strong>keep work moving.</strong>
                            </h2>

                            <p>
                                Dropgear IO brings AI and workflow automation
                                together to help businesses structure repetitive
                                operational processes.
                            </p>

                        </div>


                        <div className="automation-capabilities-grid">

                            {/* CARD 01 */}

                            <div className="automation-capability-card featured">

                                <div className="automation-capability-icon">
                                    <FiCpu />
                                </div>

                                <span className="automation-card-number">
                                    01
                                </span>

                                <h3>
                                    Intelligent Workflow Automation
                                </h3>

                                <p>
                                    Create workflows that combine business logic
                                    with AI-powered processing to handle defined
                                    operational tasks.
                                </p>

                                <ul>
                                    <li>
                                        <FiCheckCircle />
                                        Workflow orchestration
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        AI-assisted processing
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Conditional actions
                                    </li>
                                </ul>

                            </div>


                            {/* CARD 02 */}

                            <div className="automation-capability-card">

                                <div className="automation-capability-icon">
                                    <FiRepeat />
                                </div>

                                <span className="automation-card-number">
                                    02
                                </span>

                                <h3>
                                    Repetitive Task Automation
                                </h3>

                                <p>
                                    Reduce repetitive manual steps by creating
                                    structured workflows for recurring business
                                    activities.
                                </p>

                                <ul>
                                    <li>
                                        <FiCheckCircle />
                                        Recurring processes
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Task routing
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Automated actions
                                    </li>
                                </ul>

                            </div>


                            {/* CARD 03 */}

                            <div className="automation-capability-card">

                                <div className="automation-capability-icon">
                                    <FiGitBranch />
                                </div>

                                <span className="automation-card-number">
                                    03
                                </span>

                                <h3>
                                    Decision Workflows
                                </h3>

                                <p>
                                    Build workflows that evaluate defined conditions
                                    and determine what should happen next.
                                </p>

                                <ul>
                                    <li>
                                        <FiCheckCircle />
                                        Conditional logic
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Process routing
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Human review points
                                    </li>
                                </ul>

                            </div>


                            {/* CARD 04 */}

                            <div className="automation-capability-card">

                                <div className="automation-capability-icon">
                                    <FiMessageSquare />
                                </div>

                                <span className="automation-card-number">
                                    04
                                </span>

                                <h3>
                                    Communication Automation
                                </h3>

                                <p>
                                    Connect business workflows with communication
                                    steps so teams and customers receive the right
                                    information at the right stage.
                                </p>

                                <ul>
                                    <li>
                                        <FiCheckCircle />
                                        Notifications
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        AI-assisted responses
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Workflow messaging
                                    </li>
                                </ul>

                            </div>


                            {/* CARD 05 */}

                            <div className="automation-capability-card">

                                <div className="automation-capability-icon">
                                    <FiDatabase />
                                </div>

                                <span className="automation-card-number">
                                    05
                                </span>

                                <h3>
                                    Data Workflow Automation
                                </h3>

                                <p>
                                    Move, process, organize, and work with business
                                    information as part of structured automated flows.
                                </p>

                                <ul>
                                    <li>
                                        <FiCheckCircle />
                                        Information processing
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Data transformation
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Workflow updates
                                    </li>
                                </ul>

                            </div>


                            {/* CARD 06 */}

                            <div className="automation-capability-card">

                                <div className="automation-capability-icon">
                                    <FiLayers />
                                </div>

                                <span className="automation-card-number">
                                    06
                                </span>

                                <h3>
                                    Connected Workflows
                                </h3>

                                <p>
                                    Bring different workflow steps and business
                                    systems together into a single operational process.
                                </p>

                                <ul>
                                    <li>
                                        <FiCheckCircle />
                                        System connections
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Workflow handoffs
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Process coordination
                                    </li>
                                </ul>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            HOW AUTOMATION WORKS
        ===================================================== */}

                <section className="automation-how-section">

                    <div className="automation-container">

                        <div className="automation-section-heading">

                            <span>
                                HOW AI AUTOMATION WORKS
                            </span>

                            <h2>
                                From trigger to
                                <br />
                                <strong>completed workflow.</strong>
                            </h2>

                            <p>
                                A practical automation flow connects an event,
                                intelligence, workflow logic, and action into
                                one continuous process.
                            </p>

                        </div>


                        <div className="automation-process">

                            <div className="automation-process-step">

                                <span>01</span>

                                <div className="automation-process-icon">
                                    <FiActivity />
                                </div>

                                <h3>
                                    Trigger
                                </h3>

                                <p>
                                    A defined event starts the workflow.
                                </p>

                            </div>


                            <div className="automation-process-connector">
                                <FiArrowRight />
                            </div>


                            <div className="automation-process-step">

                                <span>02</span>

                                <div className="automation-process-icon">
                                    <FiDatabase />
                                </div>

                                <h3>
                                    Collect
                                </h3>

                                <p>
                                    Relevant information is brought into the process.
                                </p>

                            </div>


                            <div className="automation-process-connector">
                                <FiArrowRight />
                            </div>


                            <div className="automation-process-step">

                                <span>03</span>

                                <div className="automation-process-icon">
                                    <FiCpu />
                                </div>

                                <h3>
                                    Understand
                                </h3>

                                <p>
                                    AI processes information and business context.
                                </p>

                            </div>


                            <div className="automation-process-connector">
                                <FiArrowRight />
                            </div>


                            <div className="automation-process-step">

                                <span>04</span>

                                <div className="automation-process-icon">
                                    <FiGitBranch />
                                </div>

                                <h3>
                                    Decide
                                </h3>

                                <p>
                                    Defined workflow logic determines the next step.
                                </p>

                            </div>


                            <div className="automation-process-connector">
                                <FiArrowRight />
                            </div>


                            <div className="automation-process-step">

                                <span>05</span>

                                <div className="automation-process-icon">
                                    <FiZap />
                                </div>

                                <h3>
                                    Act
                                </h3>

                                <p>
                                    The workflow performs the defined action.
                                </p>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            USE CASES
        ===================================================== */}

                <section className="automation-usecases">

                    <div className="automation-container">

                        <div className="automation-section-heading centered">

                            <span>
                                WHERE AUTOMATION CAN HELP
                            </span>

                            <h2>
                                Automate the processes
                                <br />
                                <strong>that happen every day.</strong>
                            </h2>

                            <p>
                                Different businesses have different repetitive
                                workflows. The automation layer can be designed
                                around the specific process being addressed.
                            </p>

                        </div>


                        <div className="automation-usecase-grid">

                            <div className="automation-usecase-card">

                                <FiShoppingBag />

                                <h3>
                                    Retail & Ecommerce
                                </h3>

                                <p>
                                    Order workflows, customer requests,
                                    product information, and operational tasks.
                                </p>

                                <span>01</span>

                            </div>


                            <div className="automation-usecase-card">

                                <FiHeart />

                                <h3>
                                    Healthcare
                                </h3>

                                <p>
                                    Administrative workflows, information handling,
                                    scheduling processes, and internal assistance.
                                </p>

                                <span>02</span>

                            </div>


                            <div className="automation-usecase-card">

                                <FiTruck />

                                <h3>
                                    Logistics
                                </h3>

                                <p>
                                    Operational coordination, status workflows,
                                    information routing, and process updates.
                                </p>

                                <span>03</span>

                            </div>


                            <div className="automation-usecase-card">

                                <FiDollarSign />

                                <h3>
                                    Finance
                                </h3>

                                <p>
                                    Information workflows, document processes,
                                    internal requests, and structured task flows.
                                </p>

                                <span>04</span>

                            </div>


                            <div className="automation-usecase-card">

                                <FiBriefcase />

                                <h3>
                                    Professional Services
                                </h3>

                                <p>
                                    Client workflows, internal processes,
                                    document handling, and team coordination.
                                </p>

                                <span>05</span>

                            </div>


                            <div className="automation-usecase-card">

                                <FiBox />

                                <h3>
                                    Enterprise Operations
                                </h3>

                                <p>
                                    Cross-team workflows, operational requests,
                                    approvals, and process coordination.
                                </p>

                                <span>06</span>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            WHY AI AUTOMATION
        ===================================================== */}

                <section className="automation-benefits">

                    <div className="automation-container">

                        <div className="automation-benefits-grid">

                            <div className="automation-benefits-content">

                                <span>
                                    WHY AI AUTOMATION
                                </span>

                                <h2>
                                    Less repetitive work.
                                    <br />
                                    <strong>More room for meaningful work.</strong>
                                </h2>

                                <p>
                                    Automation is not only about removing manual
                                    steps. It is about creating a more consistent
                                    way for work to move through an organization.
                                </p>

                                <a
                                    href="/contact"
                                    className="automation-primary-btn"
                                >
                                    Discuss Your Workflow
                                    <FiArrowRight />
                                </a>

                            </div>


                            <div className="automation-benefit-list">

                                <div>

                                    <FiClock />

                                    <section>
                                        <h3>
                                            Reduce repetitive effort
                                        </h3>

                                        <p>
                                            Automate recurring activities that consume
                                            valuable team time.
                                        </p>
                                    </section>

                                </div>


                                <div>

                                    <FiSettings />

                                    <section>
                                        <h3>
                                            Structure operational processes
                                        </h3>

                                        <p>
                                            Turn loosely connected manual steps into
                                            defined workflows.
                                        </p>
                                    </section>

                                </div>


                                <div>

                                    <FiActivity />

                                    <section>
                                        <h3>
                                            Keep work moving
                                        </h3>

                                        <p>
                                            Connect tasks, decisions, and actions so
                                            processes can progress more smoothly.
                                        </p>
                                    </section>

                                </div>


                                <div>

                                    <FiLayers />

                                    <section>
                                        <h3>
                                            Build around your process
                                        </h3>

                                        <p>
                                            Automation can be designed around the
                                            workflow your business actually follows.
                                        </p>
                                    </section>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            CTA
        ===================================================== */}

                <section className="automation-final-cta">

                    <div className="automation-final-glow"></div>

                    <div className="automation-final-content">

                        <span>
                            READY TO AUTOMATE?
                        </span>

                        <h2>
                            Find the work
                            <br />
                            worth automating.
                        </h2>

                        <p>
                            Tell Dropgear IO about a repetitive process,
                            operational bottleneck, or workflow you want
                            to improve.
                        </p>

                        <a
                            href="/contact"
                            className="automation-cta-button"
                        >
                            Start a Conversation
                            <FiArrowRight />
                        </a>

                    </div>

                </section>

            </main>

            <Footer />
        </>
    );
};

export default AIAutomation;